// =============================================================================
// MEDIA-WITH-CONTENT NOTE — Horizon `Component` port of the "Toby's tidbit" overlay-note
// behaviour. This is the JS layer of the media-with-content Horizon pilot: it reparents the
// EXACT behaviour of the bundled src/js/objects/global/image-with-text-note.js onto Horizon's
// `Component` base class, loaded as native ESM via the `@theme/*` importmap
// (snippets/horizon-importmap.liquid) rather than the webpack src/js bundle.
//
//   1. DATE   — when the merchant leaves the note date BLANK, blocks/_media-with-content-media.liquid
//               renders a hidden `<p ref="date" hidden>`; fill it with the visitor's machine LOCAL
//               date (e.g. "17th July 2026") and unhide it. A merchant-typed date is server-rendered
//               static text with no `ref`, so it is left untouched (`this.refs.date` is undefined).
//   2. REVEAL — on scroll-into-view (IntersectionObserver) run a left-to-right handwriting clip reveal
//               of the note title/content/date. Skipped (text shown immediately) when the visitor
//               prefers reduced motion or the browser lacks IntersectionObserver. The full text always
//               stays in the DOM (a11y / SEO).
//
// This element IS the overlay (`<media-with-content-note class="media-with-content__overlay">`), so it
// toggles the reveal state-classes on ITSELF. Unlike the old bundled module — which shared
// image-with-text's `image-with-text__overlay--animate` class so one module could drive both sections —
// this dedicated component owns the NAMESPACED `media-with-content__overlay--animate` class, fully
// decoupling media-with-content from image-with-text. `_media-with-content.scss` keys its reveal rules
// on that namespaced class; `is-writing` stays generic (it only ever activates in combination with the
// namespaced `--animate` class, so it cannot cross-match image-with-text's note).
//
// `type="module"` scripts DEFER and run AFTER Motto's classic head scripts. This behaviour is fully
// self-contained — it touches only its own subtree + standard browser APIs (Intl, matchMedia,
// IntersectionObserver) and depends on NO Motto load-order global — so the defer is safe.
//
// UNTRACKED new file (alongside assets/component.js + assets/utilities.js) — re-verify it exists
// before any push; a parallel agent's git op on assets/ can remove it.
// =============================================================================
import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

// Namespaced reveal state-class — _media-with-content.scss keys its clip + keyframe rules on this.
const ANIMATE = 'media-with-content__overlay--animate';
// Generic writing trigger — meaningful only combined with the namespaced ANIMATE class above.
const WRITING = 'is-writing';

// The visitor's LOCAL date in the designed "Do Month YYYY" shape (e.g. "17th July 2026").
// English brand voice, hand-assembled: Intl has no ordinal-day (1st/2nd/3rd) date format, and
// locale-dependent options reorder day/month per visitor — so pin en-GB for the month name only.
function localDateLabel(now) {
  try {
    const day = now.getDate();
    const suffix =
      day >= 11 && day <= 13
        ? 'th'
        : { 1: 'st', 2: 'nd', 3: 'rd' }[day % 10] || 'th';
    const month = new Intl.DateTimeFormat('en-GB', { month: 'long' }).format(
      now
    );
    return `${day}${suffix} ${month} ${now.getFullYear()}`;
  } catch {
    return now.toDateString();
  }
}

/**
 * The overlay "Toby's tidbit" note custom element.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [date] - the auto-local-date `<p>`, present (and `hidden`) ONLY when the
 *   merchant left the note date blank. A merchant-typed date has no `ref`, so this is undefined.
 *
 * @extends {Component<Refs>}
 */
export class MediaWithContentNote extends Component {
  // Guard against a double connectedCallback (e.g. a morph re-connect without a disconnect) re-arming
  // the reveal or re-filling the date. Mirrors the bundled module's `dataset.iwtNoteInit` idempotency.
  #armed = false;

  connectedCallback() {
    super.connectedCallback(); // populates this.refs (synchronous #updateRefs) + wires Component events
    if (this.#armed) return;
    this.#armed = true;
    this.#armReveal(); // clip first ...
    this.#fillDate(); // ... then fill, so the local date is never flashed un-clipped
  }

  /**
   * Arm the handwriting reveal. Adding the ANIMATE class clips the note text first; the
   * IntersectionObserver then runs the reveal on scroll-in. Returns early (text stays visible) under
   * reduced motion or with no IntersectionObserver — a defence-in-depth gate alongside the SCSS's own
   * `prefers-reduced-motion: no-preference` wrapper.
   */
  #armReveal() {
    if (prefersReducedMotion() || !('IntersectionObserver' in window)) return;
    this.classList.add(ANIMATE);
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add(WRITING);
          obs.unobserve(entry.target);
        });
      },
      { threshold: 0.35, rootMargin: '0px 0px -10% 0px' }
    );
    observer.observe(this);
  }

  /**
   * Fill the visitor's LOCAL date when the merchant left the note date blank (the `ref="date"` element
   * is present + `hidden`). A merchant-typed date is static server text with no `ref` — left untouched.
   */
  #fillDate() {
    const dateEl = this.refs.date;
    if (!dateEl) return; // a merchant-typed (static) date — nothing to fill
    dateEl.textContent = localDateLabel(new Date());
    dateEl.removeAttribute('hidden');
  }
}

if (!customElements.get('media-with-content-note')) {
  customElements.define('media-with-content-note', MediaWithContentNote);
}
